import { useIsMobile } from '@energiebespaarders/hooks';
import { Box, Button, Flex, Image, theme } from '@energiebespaarders/symbols';
import { margin } from '@energiebespaarders/symbols/styles/mixins';
import { MagnifyingGlass, UserCircle, X } from '@phosphor-icons/react';
import Link from 'next/link';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { enumValues } from 'src/typeHelpers';
import styled, { css } from 'styled-components';
import { useAuthModal } from '~/hooks/useAuthModal';
import useMediaQuery from '~/hooks/useMediaQuery';
import { useNotificationCounter } from '~/hooks/useNotificationCounter';
import useIsAuthenticated from '../../hooks/useIsAuthenticated';
import { padding } from '../../styles/mixins';
import { legacyNavMenuLinks, MainNavItem, mainNavTitles, navMenuLinks } from './constants';
import NavActionButton from './NavActionButton';
import NavMenu from './NavMenu';
import SearchMenu from './SearchMenu';
import Logo from '/public/img/logo_WEB.svg';

export const StyledNavbar = styled(Flex)`
  position: relative;
  width: 100%;
  height: ${x => x.theme.fixedSizes.navHeight};
  background: white;
  z-index: ${x => x.theme.z.nav};
  border-bottom: 1px solid ${x => x.theme.colors.grayLight};
`;

export const StyledNavLogo = styled(Image)`
  ${padding(2.5, 'l')};
  ${padding(1.5, 'y')};
  height: ${x => x.theme.fixedSizes.navHeight};
`;

export const StyledNavItem = styled(Button).attrs({
  mb: 0,
  mr: 0,
  minimal: true,
  bgColor: 'grayDarker',
})`
  position: relative;
  border-radius: 3px;
  font-weight: 500;

  &:hover,
  &:focus,
  &:active {
    border-bottom: 3px solid ${x => x.theme.colors.green};
    background: ${x => x.theme.colors.grayLighter};
  }

  ${x =>
    x.isActive &&
    css`
      border-bottom: 3px solid ${x.theme.colors.green};
      background: ${x.theme.colors.grayLighter};
    `}
`;

export const NotificationCounter = styled.span`
  ${margin(1, 'l')};
  position: absolute;
  top: 3px;
  right: 3px;
  display: inline-block;
  width: 1.2em;
  height: 1.2em;
  line-height: 1.2;
  font-size: ${x => x.theme.type.scale[7]};
  background: ${x => x.theme.colors.red};
  text-align: center;
  color: white;
  border-radius: 100px;
  font-weight: 600;
`;

const ClickOutsideComponent: React.FC<{ onClickOutside: (...args: any) => void }> = ({
  onClickOutside,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Function to handle click event
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onClickOutside(); // Call the outside click handler
      }
    };

    // Bind the event listener to the document
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup the event listener on unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClickOutside]);

  return <div ref={wrapperRef}>{children}</div>;
};

interface NavbarProps {
  sidebarNavOpen: boolean;
  toggleSidebarNav: () => void;
}

const HiddenNavLinks = styled.div`
  position: absolute !important;
  left: -9999px !important;
  width: 1px !important;
  height: 1px !important;
  overflow: hidden !important;
  pointer-events: none !important;
  visibility: hidden !important;
`;

const SeoNavLinks = () => {
  const mainLinks = Object.values(MainNavItem).flatMap(menuKey =>
    navMenuLinks[menuKey].flatMap(links =>
      links.map(link =>
        link.to ? (
          <a key={link.to + '-main'} href={link.to} tabIndex={-1}>
            {typeof link.label === 'string' ? link.label : ''}
          </a>
        ) : null,
      ),
    ),
  );
  const legacyLinks = Object.values(MainNavItem).flatMap(menuKey =>
    legacyNavMenuLinks[menuKey].flatMap(links =>
      links.map(link =>
        link.to ? (
          <a key={link.to + '-legacy'} href={link.to} tabIndex={-1}>
            {typeof link.label === 'string' ? link.label : ''}
          </a>
        ) : null,
      ),
    ),
  );
  return <HiddenNavLinks aria-hidden="true">{[...mainLinks, ...legacyLinks]}</HiddenNavLinks>;
};

const Navbar: React.FC<NavbarProps> = ({ sidebarNavOpen, toggleSidebarNav }) => {
  const isAuthenticated = useIsAuthenticated();
  const isMobile = useIsMobile();
  const isTablet = useMediaQuery('lg');
  const isDesktop = !isMobile && !isTablet;
  const toggleAuthModal = useAuthModal();
  const { notificationCount } = useNotificationCounter();

  const [activeMenu, setActiveMenu] = useState<MainNavItem | ''>('');
  const [isSearchActive, setIsSearchActive] = useState(false);

  const handleCloseMenu = useCallback(() => {
    setActiveMenu('');
    setIsSearchActive(false);
  }, []);

  const handleOpenSearch = useCallback(() => {
    setIsSearchActive(true);
    setActiveMenu('');
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent, action: 'open' | 'close' | 'toggle') => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (action === 'open') {
        handleOpenSearch();
      } else if (action === 'close') {
        handleCloseMenu();
      } else if (action === 'toggle') {
        toggleSidebarNav();
      }
    }
  };

  const renderMenu = () => {
    if (isSearchActive) return <SearchMenu key="menu" handleClose={handleCloseMenu} />;
    if (activeMenu) return <NavMenu activeMenu={activeMenu} handleClose={handleCloseMenu} />;
    return null;
  };

  const renderLoginButton = () => (
    <StyledNavItem
      onMouseEnter={() => setActiveMenu('')}
      onClick={() => {
        toggleAuthModal(true);
        setIsSearchActive(false);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleAuthModal(true);
          setIsSearchActive(false);
        }
      }}
      data-testid="nav-login"
      role="button"
      tabIndex={0}
      aria-label="Inloggen"
    >
      <UserCircle
        weight="duotone"
        style={{ verticalAlign: 'text-bottom' }}
        size={24}
        aria-hidden="true"
      />
    </StyledNavItem>
  );

  return (
    <ClickOutsideComponent onClickOutside={handleCloseMenu}>
      <StyledNavbar
        alignItems="center"
        role="navigation"
        aria-label="Hoofdnavigatie"
        style={activeMenu && isDesktop ? { borderBottom: 'none' } : undefined}
      >
        <Box width={['180px', '240px', '300px']}>
          <Link href="/">
            <a aria-label="Terug naar de startpagina">
              <StyledNavLogo
                src={Logo}
                alt="Energiebespaarders logo"
                title="Terug naar de startpagina"
              />
            </a>
          </Link>
        </Box>
        <Box width={['calc(100% - 180px)', 'calc(100% - 240px)', 'calc(100% - 300px)']} px={1}>
          <Flex justifyContent="flex-end" alignItems="center">
            {isDesktop &&
              enumValues(MainNavItem)
                .filter(navItem =>
                  isAuthenticated.asCustomer ? true : navItem !== MainNavItem.Account,
                )
                .map((navItem, index) => (
                  <React.Fragment key={`nav-${navItem}-${index}`}>
                    <StyledNavItem
                      onMouseEnter={() => setActiveMenu(navItem)}
                      onClick={() => {
                        setIsSearchActive(false);
                        setActiveMenu(activeMenu === navItem ? '' : navItem);
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsSearchActive(false);
                          setActiveMenu(activeMenu === navItem ? '' : navItem);
                        }
                      }}
                      px={3}
                      isActive={activeMenu === navItem}
                      style={{ whiteSpace: 'nowrap' }}
                      data-testid={`nav-${navItem}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={activeMenu === navItem}
                      aria-haspopup="true"
                      aria-controls={`nav-menu-${navItem}`}
                    >
                      {mainNavTitles[navItem]}
                      {navItem === MainNavItem.Account && notificationCount > 0 && (
                        <NotificationCounter
                          aria-label={`${notificationCount} nieuwe notificaties`}
                        >
                          {notificationCount}
                        </NotificationCounter>
                      )}
                    </StyledNavItem>
                  </React.Fragment>
                ))}

            {!isMobile && <NavActionButton mx={1} />}

            {!isAuthenticated.asCustomer && isDesktop && renderLoginButton()}
            {isDesktop && (
              <StyledNavItem
                onClick={isSearchActive ? handleCloseMenu : handleOpenSearch}
                onKeyDown={(e: React.KeyboardEvent) =>
                  handleKeyDown(e, isSearchActive ? 'close' : 'open')
                }
                data-testid="nav-search"
                role="button"
                tabIndex={0}
                aria-label={isSearchActive ? 'Sluit zoekveld' : 'Zoeken'}
                aria-expanded={isSearchActive}
                aria-controls="search-menu"
              >
                {isSearchActive ? (
                  <X
                    weight="bold"
                    color={theme.colors.red}
                    style={{ cursor: 'pointer' }}
                    width={24}
                    aria-hidden="true"
                  />
                ) : (
                  <MagnifyingGlass
                    weight="regular"
                    color={theme.colors.grayDarker}
                    style={{ cursor: 'pointer' }}
                    size={24}
                    aria-hidden="true"
                  />
                )}
              </StyledNavItem>
            )}

            {!isDesktop && (
              <StyledNavItem
                onClick={toggleSidebarNav}
                onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, 'toggle')}
                label={sidebarNavOpen ? 'Sluiten' : 'Menu'}
                data-testid="nav-mobileMenu"
                role="button"
                tabIndex={0}
                aria-label={sidebarNavOpen ? 'Sluit menu' : 'Open menu'}
                aria-expanded={sidebarNavOpen}
                aria-controls="mobile-menu"
              />
            )}
          </Flex>
        </Box>

        {isDesktop && renderMenu()}
        {isDesktop && <SeoNavLinks />}
      </StyledNavbar>
    </ClickOutsideComponent>
  );
};

export default Navbar;
